import React, { useState, useEffect } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ContactsOutlinedIcon, UploadFileOutlinedIcon } from 'icons/materialIcons';

const TeamV2AddLeadsModal = ({
    isOpen,
    onClose,
    onSelect,
    anchorEl,
    offset = 10, // Reduced offset to position modal higher
    horizontalOffset = 0
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });

    useEffect(() => {
        if (isOpen && anchorEl) {
            const calculatePosition = () => {
                const anchorRect = anchorEl.getBoundingClientRect();
                const viewportHeight = window.innerHeight;
                const modalHeight = 160; // Approximate modal height

                let top = anchorRect.bottom + offset;
                let left = anchorRect.left + (anchorRect.width / 2) + horizontalOffset;

                // Ensure modal doesn't go off-screen on mobile
                if (isMobile) {
                    left = Math.min(Math.max(160, left), window.innerWidth - 160);
                }

                // Adjust if modal would go off bottom of screen
                if (top + modalHeight > viewportHeight) {
                    top = anchorRect.top - modalHeight - offset;
                }

                setModalPosition({ top, left });
            };

            calculatePosition();
            window.addEventListener('resize', calculatePosition);
            return () => window.removeEventListener('resize', calculatePosition);
        }
    }, [isOpen, anchorEl, offset, horizontalOffset, isMobile]);

    if (!isOpen) return null;

    const handleOverlayClick = (e) => {
        e.stopPropagation();
        onClose();
    };

    const optionStyles = {
        display: 'flex',
        alignItems: 'center',
        padding: isMobile ? '0.75rem' : '1rem',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        borderRadius: '0.5rem',
        '&:hover': {
            backgroundColor: '#f5f5f5',
        },
    };

    const iconStyles = {
        color: '#828282',
        fontSize: isMobile ? '1.25rem' : '1.5rem',
        marginRight: isMobile ? '0.75rem' : '1rem',
    };

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1100,
                backgroundColor: 'transparent',
            }}
            onClick={handleOverlayClick}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: `${modalPosition.top}px`,
                    left: `${modalPosition.left}px`,
                    transform: 'translateX(-50%)',
                    backgroundColor: '#fff',
                    borderRadius: '0.5rem',
                    width: isMobile ? 'calc(100% - 32px)' : '90%',
                    maxWidth: '20rem',
                    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08), 0px 2px 8px rgba(0, 0, 0, 0.04)',
                    margin: isMobile ? '0 16px' : 0,
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {/* Create New Lead Option */}
                    <Box
                        sx={optionStyles}
                        onClick={() => {
                            console.log('TeamAddLeadsModal: Create New Lead clicked');
                            onSelect('create');
                        }}
                    >
                        <ContactsOutlinedIcon sx={iconStyles} />
                        <Box>
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    fontSize: isMobile ? '0.875rem' : '0.9rem',
                                    color: '#333',
                                }}
                            >
                                Create New Lead
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: isMobile ? '0.75rem' : '0.8rem',
                                    color: '#828282',
                                }}
                            >
                                Manually add a lead
                            </Typography>
                        </Box>
                    </Box>

                    {/* Import via CSV Option */}
                    <Box
                        sx={optionStyles}
                        onClick={() => onSelect('import')}
                    >
                        <UploadFileOutlinedIcon sx={iconStyles} />
                        <Box>
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    fontSize: isMobile ? '0.875rem' : '0.9rem',
                                    color: '#333',
                                }}
                            >
                                Import via CSV
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: isMobile ? '0.75rem' : '0.8rem',
                                    color: '#828282',
                                }}
                            >
                                Add leads via CSV file
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default TeamV2AddLeadsModal;
